/**
 * PartnerPage — generic data-driven version of the original
 * /travel-preview paste-up. Reads its content from
 * `/api/partner-pages/{slug}` and renders the same hero / trust strip
 * / product grid / member-perk math / coming-soon strip / partner-note
 * layout that the BringFido preview used.
 *
 * URL: /p/:slug
 *
 * If the slug doesn't exist or is unpublished → 404 fallback.
 */
import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import {
  Hotel, Plane, Car, MapPin, Star, Heart, Coins, ShieldCheck,
  ArrowRight, Dog, PawPrint, Calendar, Loader2,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ICONS = { Hotel, Plane, Car, MapPin, Star, Heart, Coins, ShieldCheck, Dog, PawPrint, Calendar };

const Pill = ({ children, color = 'amber' }) => (
  <span
    className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium ${
      color === 'green' ? 'bg-green-100 text-green-800'
      : color === 'blue' ? 'bg-blue-100 text-blue-800'
      : 'bg-amber-100 text-amber-800'
    }`}
  >{children}</span>
);

const themeMap = {
  amber:  { tint: 'amber',  bg: 'from-amber-50/40',  textHero: 'text-amber-700',  btn: 'bg-amber-600 hover:bg-amber-700',  card: 'border-amber-300 bg-gradient-to-br from-amber-50 to-amber-100/50', strip: 'border-amber-200/50 bg-amber-50/60' },
  blue:   { tint: 'blue',   bg: 'from-blue-50/40',   textHero: 'text-blue-700',   btn: 'bg-blue-600 hover:bg-blue-700',   card: 'border-blue-300 bg-gradient-to-br from-blue-50 to-blue-100/50',     strip: 'border-blue-200/50 bg-blue-50/60' },
  rose:   { tint: 'rose',   bg: 'from-rose-50/40',   textHero: 'text-rose-700',   btn: 'bg-rose-600 hover:bg-rose-700',   card: 'border-rose-300 bg-gradient-to-br from-rose-50 to-rose-100/50',     strip: 'border-rose-200/50 bg-rose-50/60' },
  emerald:{ tint: 'emerald',bg: 'from-emerald-50/40',textHero: 'text-emerald-700',btn: 'bg-emerald-600 hover:bg-emerald-700', card: 'border-emerald-300 bg-gradient-to-br from-emerald-50 to-emerald-100/50', strip: 'border-emerald-200/50 bg-emerald-50/60' },
};

export default function PartnerPage() {
  const { slug } = useParams();
  const [page, setPage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    axios
      .get(`${API}/partner-pages/${slug}`)
      .then((r) => setPage(r.data))
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [slug]);

  if (loading) return (
    <div className="min-h-[60vh] flex items-center justify-center">
      <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
    </div>
  );

  if (notFound || !page) return (
    <div className="min-h-[60vh] flex flex-col items-center justify-center text-center px-4">
      <h2 className="text-2xl font-heading font-medium mb-2">Partner page not found</h2>
      <p className="text-sm text-muted-foreground mb-4">
        This partner page either doesn't exist or hasn't been published yet.
      </p>
      <Link to="/" className="text-sm text-amber-700 hover:underline">← Back to AADC</Link>
    </div>
  );

  const t = themeMap[page.theme_color] || themeMap.amber;

  const handleCtaClick = (targetUrl, context) => {
    fetch(`${API}/travel-credits/click`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        partner: page.slug,
        target_url: targetUrl,
        context: context || `partner-page:${page.slug}`,
      }),
    }).catch(() => {});
    window.open(targetUrl, '_blank', 'noopener');
  };

  return (
    <div className={`min-h-screen bg-gradient-to-b ${t.bg} to-background`}>
      {/* ============ HERO ============ */}
      <section className="container mx-auto px-4 pt-12 pb-10">
        <div className="max-w-3xl">
          <div className="flex items-center gap-2 mb-4">
            <Badge className="bg-amber-100 text-amber-800 border-amber-200">
              <Heart className="w-3 h-3 mr-1" />
              AADC × {page.partner_name}
            </Badge>
            <Pill color="blue">{page.category_label}</Pill>
            {page.status === 'draft' && <Pill color="rose">DRAFT</Pill>}
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-heading font-medium tracking-tight leading-[1.05]">
            {page.hero_title_line1}<br />
            <span className={t.textHero}>{page.hero_title_line2}</span>
          </h1>
          <p className="text-base sm:text-lg text-muted-foreground mt-6 max-w-2xl">
            {page.hero_subtitle}
          </p>
          <div className="flex items-center gap-3 mt-7">
            <Button
              size="lg"
              className={t.btn}
              onClick={() => handleCtaClick(page.partner_url, 'hero-primary')}
              data-testid="partner-hero-cta"
            >
              {page.primary_cta_label}
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
            {page.secondary_cta_label && (
              <a
                href={page.secondary_cta_url || '#'}
                className="text-sm text-muted-foreground hover:text-foreground"
                {...(page.secondary_cta_url?.startsWith('http') ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
              >
                {page.secondary_cta_label}
              </a>
            )}
          </div>
        </div>
      </section>

      {/* ============ TRUST STRIP ============ */}
      {page.trust_stats?.length > 0 && (
        <section className={`border-y py-6 ${t.strip}`}>
          <div className={`container mx-auto px-4 grid grid-cols-2 sm:grid-cols-${Math.min(page.trust_stats.length, 4)} gap-6 text-center`}>
            {page.trust_stats.map((s, i) => (
              <div key={i}>
                <div className={`text-2xl font-heading font-medium ${t.textHero}`}>{s.value}</div>
                <div className="text-xs text-muted-foreground mt-1">{s.label}</div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* ============ PRODUCTS ============ */}
      {page.products?.length > 0 && (
        <section className="container mx-auto px-4 py-12">
          <div className="flex items-end justify-between mb-6">
            <div>
              <h2 className="text-2xl font-heading font-medium flex items-center gap-2">
                <Hotel className={`w-6 h-6 ${t.textHero}`} />
                {page.products_heading}
              </h2>
              {page.products_subheading && (
                <p className="text-sm text-muted-foreground mt-1">{page.products_subheading}</p>
              )}
            </div>
            <a
              href={page.partner_url}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => { e.preventDefault(); handleCtaClick(page.partner_url, 'product-grid-see-all'); }}
              className={`text-sm font-medium ${t.textHero} hover:underline whitespace-nowrap`}
            >
              See more on {page.partner_name} →
            </a>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {page.products.map((p) => (
              <Card key={p.id} className="overflow-hidden card-interactive">
                {p.image && (
                  <div className="relative">
                    <img src={p.image} alt={p.name} className="w-full h-44 object-cover" loading="lazy" />
                  </div>
                )}
                <CardContent className="p-4 space-y-3">
                  <div>
                    <h3 className="font-heading font-medium leading-tight">{p.name}</h3>
                    {(p.subtitle || p.rating) && (
                      <p className="text-xs text-muted-foreground flex items-center gap-1 mt-1">
                        {p.subtitle && <><MapPin className="w-3 h-3" />{p.subtitle}</>}
                        {p.rating && <><Star className="w-3 h-3 ml-2 text-amber-500 fill-amber-500" />{p.rating}</>}
                      </p>
                    )}
                  </div>
                  {p.pills?.length > 0 && (
                    <div className="flex flex-wrap gap-1.5">
                      {p.pills.map((pl, i) => <Pill key={i}>{pl}</Pill>)}
                    </div>
                  )}
                  {p.perks?.length > 0 && (
                    <ul className="text-xs text-muted-foreground space-y-1">
                      {p.perks.map((pk, i) => (
                        <li key={i} className="flex items-start gap-1.5">
                          <PawPrint className={`w-3 h-3 mt-0.5 ${t.textHero} shrink-0`} />
                          <span>{pk}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                  <Button
                    className={`w-full ${t.btn}`}
                    size="sm"
                    onClick={() => handleCtaClick(p.cta_url, `product:${p.id}`)}
                    data-testid={`partner-product-cta-${p.id}`}
                  >
                    {p.cta_label} <ArrowRight className="w-3.5 h-3.5 ml-1" />
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        </section>
      )}

      {/* ============ MEMBER PERK ============ */}
      {page.member_perk_body && (
        <section className="container mx-auto px-4 pb-12">
          <Card className={t.card}>
            <CardContent className="p-6 sm:p-8">
              <div className="flex items-start gap-4">
                <div className={`hidden sm:flex w-12 h-12 rounded-full ${t.btn.split(' ')[0]} items-center justify-center flex-shrink-0`}>
                  <Coins className="w-6 h-6 text-white" />
                </div>
                <div className="flex-1">
                  <h2 className="text-xl sm:text-2xl font-heading font-medium mb-2">{page.member_perk_heading}</h2>
                  <p className="text-sm sm:text-base text-muted-foreground whitespace-pre-line">{page.member_perk_body}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </section>
      )}

      {/* ============ COMING SOON ============ */}
      {page.coming_soon?.length > 0 && (
        <section className="container mx-auto px-4 pb-14">
          <h2 className="text-2xl font-heading font-medium mb-2 flex items-center gap-2">
            <Calendar className={`w-6 h-6 ${t.textHero}`} />
            Also coming via {page.partner_name}
          </h2>
          <div className="grid md:grid-cols-3 gap-4 mt-4">
            {page.coming_soon.map((cs, i) => {
              const Icon = ICONS[cs.icon] || PawPrint;
              return (
                <Card key={i} className="border-dashed">
                  <CardContent className="p-5">
                    <div className="flex items-center gap-2 mb-2">
                      <Icon className={`w-5 h-5 ${t.textHero}`} />
                      <h3 className="font-heading font-medium">{cs.label}</h3>
                      <Pill color="blue">Soon</Pill>
                    </div>
                    <p className="text-sm text-muted-foreground">{cs.sub}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </section>
      )}

      {/* ============ PARTNER NOTE ============ */}
      {page.partner_note && (
        <section className="container mx-auto px-4 pb-16">
          <Card className="border-blue-200 bg-blue-50/50">
            <CardContent className="p-5 flex items-start gap-3">
              <ShieldCheck className="w-5 h-5 text-blue-700 mt-0.5 shrink-0" />
              <div className="text-sm text-blue-800 whitespace-pre-line">{page.partner_note}</div>
            </CardContent>
          </Card>
        </section>
      )}

      {/* ============ COMMUNITY LINK ============ */}
      {page.subgroup_id && (
        <section className="container mx-auto px-4 pb-14">
          <Card>
            <CardContent className="p-5 flex items-center justify-between gap-4 flex-wrap">
              <div>
                <h3 className="font-heading font-medium">Discuss with the community</h3>
                <p className="text-sm text-muted-foreground">
                  Real member experiences with {page.partner_name}. Run jointly with the partner.
                </p>
              </div>
              <Link to={`/communities/${page.slug}`}>
                <Button variant="outline">
                  Visit {page.partner_name} community <ArrowRight className="w-3.5 h-3.5 ml-1" />
                </Button>
              </Link>
            </CardContent>
          </Card>
        </section>
      )}
    </div>
  );
}
